import { CONFIG } from '../../appConfig';
import STATE from '../../constants/state';

export const RESERVATIONS = 'reservations/';
const CHECKIN = 'oci/';
const SKIPTHECOUNTER = 'stc/';

export default {
  /**
   * @desc Adds an additional driver to the reservation
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
    *   address: {
    *    "address_type": "HOME",
    *    "city": "Chicago",
    *    "country_code": "US",
    *    "country_name": "United States",
    *    "country_subdivision_code": "IL",
    *    "country_subdivision_name": "Illinois",
    *    "postal": "60601",
    *    "street_addresses": [
    *      "300 E Randolph st"
    *     ]
    *   }
    *  .
    *  .
    *  .
    * }
   * services.gma.reservations.addAdditionalDriver({payload});
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  addAdditionalDriver(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}addAdditionalDriver`,
      data: payload,
      params
    };
  },

  /**
   * @desc Adds additional drivers to the reservation
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   * [
   *  {
   *   "first_name": "HAPPYADDDRIVER",
   *   "last_name": "TESTERA",
   *  },
   *  {
   *    "first_name": "HAPPYADDDRIVER",
   *    "last_name": "TESTERB",
   *  }
   * ]
   * services.gma.reservations.addAdditionalDrivers({payload});
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  addAdditionalDrivers(data = {}) {
    const { payload = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}addAdditionalDrivers`,
      data: payload
    };
  },

  /**
   * @desc Adds an additional driver to the reservation using first and last name
   * @param {Object} data {params, endpoint}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const params = {
    *  firstName: "name",
    *  lastName: "surname"
    * }
    *
   * services.gma.reservations.addAdditionalDriverByName({params});
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  addAdditionalDriverByName(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}addAdditionalDriverByName`,
      params
    };
  },


  /**
   * @desc Associate profile with reservation
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.associate();
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  associate(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}associate`,
      params
    };
  },

  /**
   * @desc Associate profile with an array of confirmation numbers
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const params = {
   *  confirmationNumbers: ['1206669885', '1600002333', '160000233']
   * };
   * services.gma.reservations.associateAll({params});
   * // dispatched response is NOT stored in redux store
   */
  associateAll(data = {}) {
    const { params = {} } = data;
    const { confirmationNumbers = [] } = params;
    params.confirmationNumbers = confirmationNumbers.join(',');
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}associate/all`,
      params
    };
  },

  /**
   * @desc Associate profile with existing reservation
   * @returns {Object} data {params}
   * @memberof services.gma.reservations
   * @example
   * // this defaults to false so only needs to be included if it should be `true`
   * const params = {ignoreContractConflicts: false};
   * services.gma.reservations.associateModify({params});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  associateModify(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}associate/modify`,
      params
    };
  },

  /**
   * @desc Cancel existing reservation
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   confirmation_number, // *(String)* i.e. '1701724168'
   *   prepayModify // *(Boolean)*
   * }
   * services.gma.reservations.cancel({payload});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  cancel(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}cancel`,
      data: payload,
      params
    };
  },

  /**
   * @desc Get car class details by car class code
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const data = {
   *  params = {
   *    carClassCode // *(String)* i.e. 'ECAR'
   *  },
   *  opts: {
   *    timeoutConfig: {
   *      onRequestTimeoutCallback: () => dispatch(callVehicleDetails(params, false))
   *    }
   *  }
   * }
   * services.gma.reservations.carClassDetails({params});
   * // dispatched results stored in `state.gmi.gma.reservation.car_class_details[carClassCode]`
   * // note: above should get cleared out on new reservation initiate requests via...
   * // dispatch(actions.reservations.cleanNonSessionReservationState())
   */
  carClassDetails(data = {}) {
    const { params = {} } = data;
    const statePath = !!params.redemptionDayCount ? STATE.GMA.RESERVATION.CAR_CLASS_DETAILS.concat([`${params.carClassCode}-${params.redemptionDayCount}`]) : STATE.GMA.RESERVATION.CAR_CLASS_DETAILS.concat([`${params.carClassCode}`]);
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}carClassDetails`,
      params,
      statePath: statePath
    };
  },

  /**
   * @desc Get a card submission session object
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   submission_type, // *(String)*
   *   success_url, // *(String)*
   *   failure_url, // *(String)*
   *   style_sheet_url, // *(String)*
   * };
   * services.gma.reservations.cardSubmissionSession({payload});
   * // dispatched results stored in `state.gmi.gma.reservation.card_submission_session`
   */
  cardSubmissionSession(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}cardSubmissionSession`,
      data: payload,
      params,
      statePath: STATE.GMA.RESERVATION.CARD_SUBMISSION_SESSION,
      trimRespProp: 'submission_context_data'
    };
  },

  /**
   * @desc Start the online check-in process
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const params = {
   *   onlineCheckInOrigin // *(String)*
   * }
   * services.gma.reservations.checkinStart({params});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  checkinStart(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${CHECKIN}start`,
      params
    };
  },

  /**
   * @desc Abort the online check-in process
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.checkinAbort();
   */
  checkinAbort(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${CHECKIN}abort`,
      params
    };
  },

  /**
   * @desc Clears the profile currently in GMA session
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.clearProfile();
   * // dispatched results stored in `state.gmi.session.gma.online_check_in_profile`
   */
  clearProfile(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${CHECKIN}clearProfile`,
      params
    };
  },

  /**
   * @desc Start the skip the counter process
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const params = {
   *   origin // *(String)*
   * }
   * services.gma.reservations.skipTheCounterStart({params});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  skipTheCounterStart(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${SKIPTHECOUNTER}start`,
      params
    };
  },

  /**
   * @desc Abort the skip the counter process
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.skipTheCounterAbort();
   */
  skipTheCounterAbort(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${SKIPTHECOUNTER}abort`,
      params
    };
  },

  /**
   * @desc Confirm selections in skip the counter process
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @example
   * const payload = {
   *   airline_information: {
   *     code, // *(String)*
   *     flight_number // *(String)*
   *   },
   * };
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.skipTheCounterConfirmSelections();
   */
  skipTheCounterConfirmSelections(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${SKIPTHECOUNTER}confirmSelections`,
      data: payload,
      params
    };
  },

  /**
   * @desc Initiates hosted payment (HPP) session
   * @param {Object} data {payload, params, statePath}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   success_url, // *(String)*
   *   failure_url, // *(String)*
   *   style_sheet_url, // *(String)*
   * };
   * services.gma.reservations.initiatePaymentSession({payload});
   * // dispatched results stored in the statePath provided
   */
  initiatePaymentSession(data = {}) {
    const { payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}payment/initiate`,
      data: payload,
      params,
      statePath: data.statePath
    };
  },

  /**
   * @desc Get payment details object using paymentId
   * @param {Object} data {paymentId}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.getPaymentDetailsByPaymentId({ paymentId: ZHLKSDI2454 });
   * // dispatched results stored in `state.gmi.gma.reservation.payment`
   */
  getPaymentDetailsByPaymentId(data = {}) {
    const { params = {}, paymentId = '' } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}payment/${paymentId}`,
      statePath: STATE.GMA.RESERVATION.PAYMENT,
      trimRespProp: 'payment_detail',
      params
    };
  },

  /**
   * @desc Get payment details object
   * @param {Object} data { session_id }
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.recordPaymentTransaction({ session_id: ZHLKSDI2454 });
   * // dispatched results stored in `state.gmi.gma.reservation.payment`
   */

  recordPaymentTransaction(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}payment/recordPaymentTransaction`,
      data: data,
      statePath: STATE.GMA.RESERVATION.PAYMENT
    };
  },

  /**
   * @desc Commit reservation
   * @param {Object} data {payload, params, endpoint}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   additional_information: [
   *     id, // *(String)*
   *     value // *(String)*
   *   ],
   *   airline_information: [
   *     code, // *(String)*
   *     flight_number // *(String)*
   *   ],
   *   billing_account, // *(String)*
   *   billing_account_type, // *(String)*
   *   driver_info: {
   *     first_name, // *(String)*
   *     last_name, // *(String)*
   *     email_address, // *(String)*
   *     phone: { // *(Object)*
   *       phone_number, // *(String)*
   *     },
   *     request_email_promotions // *(Boolean)*
   *   },
   *   payment_ids, // *(Array)* i.e ['123123123']
   *   payment_id, // *(String)*
   *   travel_admin: {
   *     email, // *(String)*
   *     name // *(String)*
   *   }
   * };
   * services.gma.reservations.commit({payload});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  commit(data = {}) {
    const { payload = {}, params = {}, endpoint = {} } = data;
    let extraPath = '';
    if (endpoint.online_check_in) extraPath = CHECKIN;
    if (endpoint.skip_the_counter) extraPath = SKIPTHECOUNTER;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${extraPath}commit`,
      data: payload,
      params
    };
  },

  /**
   * @desc Complete extras step of reservation
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.extrasComplete();
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  extrasComplete(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}extras/complete`,
      params
    };
  },

  /**
   * @desc Add extras to a reservation session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   extras: [
   *     {
   *       code, // *(String)* i.e. 'CST'
   *       quantity // *(Number)* i.e. 2
   *     }
   *   ]
   * };
   * services.gma.reservations.extrasUpdate({payload});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  extrasUpdate(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}extras/update`,
      data: payload,
      params
    };
  },

  /**
   * @desc Reservation initiation
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   one_way_rental, // *(Boolean)*
   *   pickup_location, // *(Object)* i.e. {cityId, peopleSoftId}
   *   pickup_location_id, // *(String)* i.e. a cityId or peopleSoftId
   *   pickup_time, // *(String)* i.e. '2018-04-16T18:30:00.000Z'
   *   return_location, // *(Object)* i.e. {cityId, peopleSoftId}
   *   return_location_id, // *(String)* i.e. a cityId or peopleSoftId
   *   return_time, // *(String)* i.e. '2018-04-16T18:30:00.000Z'
   *   renter_age, // *(String)* i.e. '25'
   *   renter_age_label, // *(String)* i.e. '25+'
   *   contract_number, // *(String)*
   *   booking_agent, // *(String)* i.e. 'BOOKING_ON_BEHALF_OF'
   *   car_class_code, // *(String)* i.e. 'ECAR'
   *   car_class_codes, // *(Array)* i.e. ['ECAR', 'PCAR']
   *   coupons, // *(Array)* i.e. ['CURREN323', 'CURREN323', 'CURREN323']
   *   last_name, // *(String)*
   *   membership_id, // *(String)*
   *   redemption_day_count, // *(Number)*
   *   ...
   * };
   * services.gma.reservations.initiate({payload});
   * // dispatched results stored in...
   * // `state.gmi.session.gbo.reservation`
   * // `state.gmi.session.gma.initiate_request` // init payload
   */
  initiate(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}initiate`,
      data: payload,
      params
    };
  },

  /**
   * @desc Reservation reInitiate call after user authenticates in vehicle page
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example services.gma.reservations.reInitiate();
   */
  reInitiate() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}reInitiate`
    };
  },

  /**
   * @desc Accept or decline meet & greet
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * payload = {
   *   meet_and_greet_accepted, // *(Boolean)*
   *   airline_information: {
   *     code, // *(String)*
   *     flight_number // *(String)*
   *   }
   * }
   * services.gma.reservations.levelOfService({payload});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  levelOfService(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}levelOfService`,
      data: payload,
      params
    };
  },

  /**
   * @desc Abort modify reservation (purely a GMA construct)
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.modifyAbort();
   * // dispatched results stored in `state.gmi.session.gma.modify` // false
   */
  modifyAbort(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}modify/abort`,
      params
    };
  },

  /**
   * @desc Start a modify reservation flow (purely a GMA construct)
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.modifyStart();
   * // dispatched results stored in `state.gmi.session.gma.modify` // true
   */
  modifyStart(data = {}) {
    const { params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}modify/start`,
      params
    };
  },

  /**
     * @desc Starts modify in a one click reservation.
     * @param {String} data {params}
     * @returns {Object}
     * @memberof services.gma.reservations
     * @example
     * const payload = {
     *   stepToModify // *(String)* i.e. 'extras'
     * }
     * services.gma.reservations.oneClickModify({params, payload});
     * // dispatched results stored in `state.gmi.session.gbo.reservation`
     */
  oneClickModify(data = {}) {
    const { params = {}, payload } = data;
    const { stepToModify } = payload;

    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `reservations/oneClick/modify/${stepToModify}`,
      withCredentials: true,
      params
    };
  },

  /**
   * @desc Flop a current reservation between prepay and pay later
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.prepayFlop();
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  prepayFlop(data = {}) {
    const { params = {} } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}prepay/flop`,
      params
    };
  },

  /**
   * @desc Update reservation session with profile updates made while reservation session was active, takes param for redirecting the user back into the res flow
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * params = {
   *   redirect // *(Boolean)*
   * }
   * services.gma.reservations.refreshProfile({params});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  refreshProfile(data = {}) {
    const { params = { redirect: false } } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}refreshProfile`,
      params
    };
  },

  /**
   * @desc Removes an additional driver from the reservation
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   first_name, // *(String)* i.e. 'Test',
   *   last_name, // *(String)* i.e. 'Tester',
   *   individualId // *(String)* i.e. 'QVBSMjAxODpyRVJXeGppUjNTVUE5YmF',
   * };
   * services.gma.reservations.removeAdditionalDriver({payload});
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  removeAdditionalDriver(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}removeAdditionalDriver`,
      data: payload,
      params
    };
  },

  /**
   * @desc Get Rental Terms and Conditions
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.rentalTermsAndConditions();
   * // dispatched results stored in `state.gmi.gma.reservation.rental_terms`
   * // note: above should get cleared out on new reservation initiate requests via...
   * // dispatch(actions.reservations.cleanNonSessionReservationState())
   */
  rentalTermsAndConditions(data = {}) {
    const { params = {} } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}rentalTermsAndConditions`,
      params,
      statePath: STATE.GMA.RESERVATION.RENTAL_TERMS
    };
  },

  /**
   * @desc Reservation retrieval
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * params = {
   *   confirmationNumber, // *(Number)*
   *   firstName, // *(String)*
   *   lastName // *(String)*
   * }
   * services.gma.reservations.retrieve({params});
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  retrieve(data = {}) {
    const { params = {} } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}retrieve`,
      params
    };
  },

  /**
   * @desc Fetches the profile information tied to a drivers license
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   country, // *(String)*
   *   driver_license_number, // *(String)*
   *   last_name // *(String)*
   * }
   * services.gma.reservations.searchProfileByDriverLicense({payload});
   * // dispatched results stored in `state.gmi.session.gma.online_check_in_profile`
   */
  searchProfileByDriverLicense(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${CHECKIN}searchProfileByDriverLicense`,
      data: payload,
      params
    };
  },

  /**
   * @desc Select a vehicle car class and add to reservation session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const params = {
   *   car_class_code, // *(String)* i.e. 'CCAR'
   * };
   * services.gma.reservations.selectCarClass(data);
   * // dispatched results stored in `state.gmi.session.gbo.reservation`
   */
  selectCarClass(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}selectCarClass`,
      data: payload,
      params
    };
  },

  /**
   * @desc Modify the currently in-session OCI profile (GMA handles different loyalty/NL scenarios)
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.updateProfile({payload});
   * // dispatched results stored in `state.gmi.session.gma.online_check_in_profile`
   */
  updateProfile(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${CHECKIN}updateProfile`,
      data: payload,
      params
    };
  },

  /**
   * @desc Retrieve upgradable car_class_codes based on vehicle selection
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * services.gma.reservations.upgradeRates();
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  upgradeRates(data = {}) {
    const { params = {} } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}upgrade/rates`,
      params
    };
  },

  /**
   * @desc Upgrade a vehicle car class and add to reservation session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   car_class_code, // *(String)* i.e. 'CCAR'
   * };
   * services.gma.reservations.upgradeSelect({payload});
   * // dispatched results stored in state.gmi.session.gbo.reservation
   */
  upgradeSelect(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}upgrade/select`,
      data: payload,
      params
    };
  },

  /**
   * @desc Retrieve rental extend eligibility ticket
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload = {
   *   ticketId // *(String)* i.e. '332668017'
   * }
   * services.gma.reservations.checkRentalExtendEligibility({params});
   * dispatched results stored in `state.gmi.gma.extend_rental.eligibility.[ticketId]`
   */
  checkRentalExtendEligibility(data = {}) {
    const { params = {}, payload } = data;
    const { ticketId } = payload;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}extend/eligibility/${ticketId}`,
      params
    };
  },
  /**
   * @desc Initiate extend rental
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticketSessionId // *(String)* i.e. '332668017'
   *   return_time: "2022-08-02T11:00"
   * }
   *
   * services.gma.reservations.initiateRentalExtend({payload, params});
   * dispatched results stored in `state.gmi.gma.extend_rental.initiate`
   */
  initiateRentalExtend(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, return_time } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}extend/initiate/${ticketSessionId}`,
      data: {return_time},
      params
    };
  },
  /**
   * @desc Commit extend rental
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *    return_time: "2022-08-02T11:00"
   *    ticketSessionId // *(String)* i.e. '332668017'
   * }
   *
   * services.gma.reservations.commitRentalExtend({payload, params});
   * dispatched results stored in `state.gmi.session.gma.rental_extend.commit`
   */
  commitRentalExtend(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, return_time } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}extend/commit/${ticketSessionId}`,
      data: {return_time},
      params
    };
  },
  /**
   * @desc Initiate emerald checkout
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   confirmationNumber // *(String)* i.e. '1802699464'
   * }
   * const params {
   *   lastName // *(String)* i.e. 'Bolton'
   * }
   *
   * services.gma.reservations.initiateEmeraldCheckout({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout`
   */
  initiateEmeraldCheckout(data = {}) {
    const { params = {}, payload } = data;
    const { confirmationNumber } = payload;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${confirmationNumber}/initiate`,
      params
    };
  },
  /**
   * @desc Select emerald checkout vehicle
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   resSessionId // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   *   vin // *(String)* i.e. '"5NPE24AF9FH229900" '
   * }
   *
   * services.gma.reservations.initiateEmeraldCheckout({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout`
   */
  selectEmeraldCheckoutVehicle(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, vin } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${ticketSessionId}/selectVehicle`,
      data: {vin},
      params
    };
  },
  /**
   * @desc Update emerald checkout vehicle products
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticketSessionId // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   *   products // *(Object)*
   * }
   *
   * services.gma.reservations.updateEmeraldCheckoutProducts({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout`
   */
  updateEmeraldCheckoutProducts(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, products } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${ticketSessionId}/updateProducts`,
      data: products,
      params
    };
  },
  /**
   * @desc Update emerald checkout payment method
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticketSessionId // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   *   payment_ids // *(Object)*
   * }
   *
   * services.gma.reservations.updateEmeraldCheckoutPaymentMethod({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout`
   */
  updateEmeraldCheckoutPaymentMethod(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, payment_ids } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${ticketSessionId}/updatePayment`,
      data: {payment_ids},
      params
    };
  },
  /**
   * @desc Commit emerald checkout
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticketSessionId // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   *   additional_infos // *(Object)*
   *   payment_ids // *(Object)*
   * }
   * services.gma.reservations.commitEmeraldCheckout({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout`
   */
  commitEmeraldCheckout(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId, additional_infos, payment_ids } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${ticketSessionId}/commit`,
      data: {additional_infos, payment_ids},
      params
    };
  },
  /**
   * @desc List emerald checkout available vehicles
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticketSessionId // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   * }
   *
   * services.gma.reservations.listEmeraldCheckoutVehicle({payload, params});
   * dispatched results stored in `state.gmi.gma.emerald_checkout.vehicles.by_session_id`
   *
   */
  listEmeraldCheckoutVehicle(data = {}) {
    const { params = {}, payload } = data;
    const { ticketSessionId } = payload;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/${ticketSessionId}/getAvailableVehicles`,
      params
    };
  },
  /**
   * @desc Cancel emerald checkout vehicle
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.reservations
   * @example
   * const payload {
   *   ticket_number // *(String)* i.e. '"3gHyI7f02PCWYnP16f_tQ4n10Hs1rTNQ" '
   * }
   * services.gma.reservations.cancelEmeraldCheckoutVehicle({payload, params});
   * the service does not return any response just status code to confirm it was successful
   */
  cancelEmeraldCheckoutVehicle(data = {}) {
    const { params = {}, payload } = data;
    const { ticket_number } = payload;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}checkout/cancelVehicle`,
      data: {ticket_number},
      params
    };
  },
  /**
  * @desc Get reservation details to View/Modify/Cancel
  * @param {Object} data {endpoint, params}
  * @returns {Object}
  * @memberof services.gma.reservations
  * @example
  * const data = {
  *   endpoint: {
  *     confirmationNumber: "1108076215", // *(String)*
  *   },
  *   params: {
  *     first_name: "JOHN", // *(String)*
  *     last_name: "SNOW" // *(String)*
  *   }
  * }
  * services.gma.reservations.details({endpoint});
  */
  details(data = {}) {
    const {endpoint = {}, params = {}} = data;
    const {confirmationNumber = ""} = endpoint;

    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}${confirmationNumber}/details`,
      params
    };
  }
};
